import React from "react";
import PropTypes from "prop-types";
import { TextField } from "@mui/material";
import { useField, useFormikContext, ErrorMessage } from "formik";
import FormikLabel from "./FormikLabel";
import { ErrorStyle } from "./ErrorStyle";

const FormikTextInput = ({
  name,
  label,
  max,
  min,
  maxLength, // ✅ New prop
  width = "100%",
  required = false,
  placeholder = "",
  disabled = false,
  onChange = () => {},
  readOnly = false,
  inputRestriction = "alphabetic",
  specialCharacters = false,
  ...props
}) => {
  const [field] = useField(name);
  const { setFieldValue } = useFormikContext();
  const handleChange = (e) => {
    let { value } = e.target;

    if (!specialCharacters) {
      if (inputRestriction === "alphanumeric") {
        value = value.replace(/[^a-zA-Z0-9\s]/g, "");
      } else if (inputRestriction === "numeric") {
        value = value.replace(/[^0-9.]/g, "");
      } else if (inputRestriction === "ifscCode") {
        value = value.replace(/[^A-Z0-9]/g, "");
      } else if (inputRestriction === "allowAll") {
        // allow everything: special chars, numbers, letters
        // no restriction needed, value remains unchanged
      } else {
        // default to only letters and space
        value = value.replace(/[^a-zA-Z\s]/g, "");
      }
    }

    if (maxLength !== undefined && value.length > maxLength) {
      value = value.substring(0, maxLength);
    }

    if (inputRestriction === "numeric") {
      const numericValue = parseInt(value || "0", 10);
      if (!isNaN(numericValue)) {
        if (min !== undefined && numericValue < min) value = String(min);
        if (max !== undefined && numericValue > max) value = String(max);
      }
    }

    setFieldValue(name, value);

    onChange({
      ...e,
      target: { ...e.target, value },
    });
  };

  return (
    <div style={{ width }}>
      {label && <FormikLabel text={label} required={required} />}
      <TextField
        {...field}
        {...props}
        placeholder={placeholder}
        fullWidth
        disabled={disabled}
        inputProps={{
          readOnly,
          maxLength,
        }}
        onChange={(e) => {
          if (!readOnly) {
            handleChange(e);
          }
        }}
        sx={{
          ...props.sx,
          "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            borderWidth: "1px",
            cursor: disabled ? "not-allowed" : "inherit",
          },
          "& .MuiInputBase-input": {
            backgroundColor: disabled || readOnly ? "#f5f5f5" : "inherit",
            padding: "8px",
            borderRadius: "8px",
            cursor: disabled || readOnly ? "not-allowed" : "inherit",
          },
        }}
      />
      <div style={ErrorStyle}>
        <ErrorMessage name={name} />
      </div>
    </div>
  );
};

// ✅ Updated PropTypes
FormikTextInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  width: PropTypes.string,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  onChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number,
  maxLength: PropTypes.number,
  inputRestriction: PropTypes.oneOf([
    "alphabetic",
    "alphanumeric",
    "numeric",
    "ifscCode",
  ]),
  specialCharacters: PropTypes.bool,
};

export default FormikTextInput;
